import React, { useRef, useState, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { coonditionContext, Condition } from '../../index';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Slide from '@mui/material/Slide';
import Fade from '@mui/material/Fade';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PlaceIcon from '@mui/icons-material/Place';
import EditIcon from '@mui/icons-material/Edit';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import PersonIcon from '@mui/icons-material/Person';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAuthProfile } from '../../app/hooks';
import { getSearchByCoordinates, setCenter as setSearchCenter } from '../../features/searchSlice';
import { activateMarker } from '../../features/activeMarkers';
import { useIsMobile } from '../../app/IsMobileContext';
import { triggerUserMarkerClick } from '../markers/UserMarker';
import { setPublicPinClicked, setAnalyticUsersClicked } from '../../features/dataProcessingSlice';
import type { PublicPinClicked, AnalyticUsersClickedType } from '../../features/dataProcessingSlice';
import { showProfile } from '../../features/dialogSlice';
import { profileApi, useGetProfilesForPointQuery } from '../../features/profilesSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { AISummary } from '../AI/AISummary';
import { RootState, useTypedSelector } from '../../app/store';
import { LeftSideComponentWithOptions } from '../../features/dataProcessingSlice';
import { FloatingContainerMorph } from './FloatingContainerMorph';
import { Patients } from '../patient/Patients';
import { PatientCard } from '../patient/PatientCard';

interface FloatingWelcomeCardProps {
  authStatus: string;
  profile: any;
  minimized: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
}

export const FloatingWelcomeCard: React.FC<FloatingWelcomeCardProps> = ({ authStatus, profile, minimized, onMinimize, onExpand }) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isProfileSuccess, profile: loggedInProfile } = useAuthProfile();

  const AIState_publicPinClicked = useTypedSelector((state: RootState) => state.dataProcessing.publicPinClicked);
  const analyticUsersClickedState: AnalyticUsersClickedType = useTypedSelector((state: RootState) => state.dataProcessing.analyticUsersClicked);
  const leftSideComponentWithOptionsState: LeftSideComponentWithOptions = useTypedSelector(
    (state: RootState) => state.dataProcessing.leftSideComponentWithOptions
  );
  const AIState_aiProfileOptions = leftSideComponentWithOptionsState.options;
  const showAccountPromotion = authStatus !== 'authenticated';

  // Keep the last valid pin so AISummary renders content during Slide exit animation
  const lastPinRef = useRef(AIState_publicPinClicked);
  if (AIState_publicPinClicked) {
    lastPinRef.current = AIState_publicPinClicked;
  }

  // ── Navigation stack for proper back behavior ──
  interface NavState { pinId: PublicPinClicked; analyticUsers: AnalyticUsersClickedType }
  const navStackRef = useRef<NavState[]>([]);
  const skipNextPushRef = useRef(false);
  const prevNavRef = useRef<NavState>({ pinId: AIState_publicPinClicked, analyticUsers: analyticUsersClickedState });

  useEffect(() => {
    const prev = prevNavRef.current;
    const curr: NavState = { pinId: AIState_publicPinClicked, analyticUsers: analyticUsersClickedState };
    if (skipNextPushRef.current) {
      skipNextPushRef.current = false;
    } else if (curr.pinId !== prev.pinId || curr.analyticUsers !== prev.analyticUsers) {
      if (curr.pinId) {
        if (prev.pinId) {
          navStackRef.current.push({ ...prev });
        }
      } else {
        navStackRef.current = [];
      }
    }
    prevNavRef.current = curr;
  }, [AIState_publicPinClicked, analyticUsersClickedState]);

  const handleBack = useCallback(() => {
    const stack = navStackRef.current;
    if (stack.length > 0) {
      const prev = stack.pop()!;
      skipNextPushRef.current = true;
      dispatch(setPublicPinClicked(prev.pinId));
      if (prev.analyticUsers !== false) {
        dispatch(setAnalyticUsersClicked(prev.analyticUsers));
      }
    } else {
      dispatch(setPublicPinClicked(false));
    }
  }, [dispatch]);

  // Get total patient count
  const { data: profilesPinData } = profileApi.useGetProfilesPinDataForDefaultPointQuery();
  const totalCount = profilesPinData?.[0]?.count_total;

  // Get members near user's pin (only when authenticated with completed profile)
  const nearbyArgs = (authStatus === 'authenticated' && loggedInProfile?.is_completed && loggedInProfile?.lat && loggedInProfile?.lng)
    ? { lat: loggedInProfile.lat, lng: loggedInProfile.lng }
    : undefined;
  const { data: nearbyData, isSuccess: isNearbySuccess } = useGetProfilesForPointQuery(nearbyArgs ?? skipToken);
  const countNearby = nearbyData?.[0]?.count_in_radius;

  const handleHomeClick = () => {
    if (!loggedInProfile?.id) return;
    dispatch(getSearchByCoordinates({ lat: loggedInProfile.lat, lng: loggedInProfile.lng }));
    dispatch(activateMarker({ id: loggedInProfile.id, disableHover: true }));
    dispatch(setPublicPinClicked(loggedInProfile.id));
  };

  const handleLocatePinClick = () => {
    if (isMobile) {
      // On mobile: collapse the bottom sheet, pan to pin, and replay the
      // DROP → BOUNCE → InfoWindow animation (same behaviour as CenterMapHome)
      onMinimize?.();
      dispatch(setSearchCenter({ lat: loggedInProfile.lat, lng: loggedInProfile.lng }));
      if (triggerUserMarkerClick) {
        triggerUserMarkerClick();
      }
    } else {
      dispatch(getSearchByCoordinates({ lat: loggedInProfile.lat, lng: loggedInProfile.lng }));
      dispatch(activateMarker({ id: loggedInProfile.id, disableHover: true }));
    }
  };

  const handleEditPinClick = () => {
    dispatch(showProfile({ isForced: true }));
  };

  const { condition } = React.useContext<Condition>(coonditionContext);
  const [showNearbyMembers, setShowNearbyMembers] = useState(false);

  const headerTitle = 'Profile';

  return (
    <>
    <FloatingContainerMorph
      minimized={minimized}
      onMinimize={onMinimize}
      onExpand={onExpand}
      onBack={AIState_publicPinClicked ? handleBack : showNearbyMembers ? () => setShowNearbyMembers(false) : undefined}
      icon={<PersonIcon sx={{ color: '#656EFF' }} />}
      title={headerTitle}
      onTitleClick={authStatus === 'authenticated' && !AIState_publicPinClicked ? handleHomeClick : undefined}
      {...(((!!AIState_publicPinClicked || showNearbyMembers) && !minimized) ? { 'data-scroll-chain': true, 'data-base-ui-swipe-ignore': true } : {})}
      sx={{
        maxHeight: (!!AIState_publicPinClicked || showNearbyMembers) && !minimized ? 'calc(100vh - 128px)' : undefined,
        overflow: (!!AIState_publicPinClicked || showNearbyMembers) && !minimized ? 'auto' : 'hidden',
        overscrollBehavior: 'none',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(164, 142, 233, 0.25)',
          borderRadius: 3,
          '&:hover': { background: 'rgba(164, 142, 233, 0.4)' },
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(164, 142, 233, 0.25) transparent',
      }}
    >
      {/* ── AISummary – slides in/out when a pin is clicked ── */}
      <Slide direction="right" in={!!AIState_publicPinClicked} timeout={400} mountOnEnter unmountOnExit>
        <Box>
          <AISummary
            bottomSheetRef={null}
            pinToUse={lastPinRef.current}
            showAccountPromotion={showAccountPromotion}
            showMessageButton={AIState_aiProfileOptions?.showMessageButton}
            showViewUserStoryAnalytics={AIState_aiProfileOptions?.showViewUserStoryAnalytics}
          />
        </Box>
      </Slide>

      {/* ── Default welcome card content ── */}
      <Fade in={!AIState_publicPinClicked} timeout={350} mountOnEnter unmountOnExit>
        <Box>
          <Stack direction="column" spacing={showNearbyMembers ? 0 : 1.5} sx={{ p: 2, ...(showNearbyMembers && { pt: 0, pb: 1 }) }}>
            {/* Unauthenticated: "You're not alone here" */}
            {authStatus === 'unauthenticated' && (
              <Stack direction="column" gap={2} sx={{ p: 2.5, backgroundColor: '#F5F3FF', borderRadius: 3, border: '1px solid #E8E4FF', boxShadow: '0 2px 8px rgba(101, 110, 255, 0.08)' }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <FavoriteIcon sx={{ color: '#E57399', fontSize: 22 }} />
                  <Typography variant="subtitle1" sx={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
                    You're not alone here
                  </Typography>
                </Stack>
                {totalCount && (
                  <Typography variant="body2" sx={{ color: '#777', fontSize: 13 }}>
                    {totalCount} patients sharing their journeys
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                  People all over the country are sharing their experiences and support.
                </Typography>
                <Button
                  variant="contained"
                  sx={{ color: 'white', backgroundColor: '#656EFF', borderRadius: 2, textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 2.5, boxShadow: '0 2px 8px rgba(101,110,255,0.25)' }}
                  onClick={() => navigate('/signin')}
                  disableElevation
                >
                  Sign in
                </Button>
              </Stack>
            )}

            {/* Username */}
            {isProfileSuccess && authStatus === 'authenticated' && loggedInProfile?.is_completed && loggedInProfile?.name_public && !showNearbyMembers && (
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{
                  border: '1px solid rgba(101, 110, 255, 0.3)',
                  borderRadius: 4,
                  px: 1,
                  py: 0.5,
                  alignSelf: 'flex-start',
                }}
              >
                <PersonIcon sx={{ color: '#656EFF', fontSize: 15 }} />
                <Typography variant="body2" sx={{ color: '#1a1a2e', fontSize: 13, fontWeight: 600 }}>
                  {loggedInProfile.name_public}
                </Typography>
              </Stack>
            )}

            {/* Members near your pin */}
            {isProfileSuccess && authStatus === 'authenticated' && loggedInProfile?.is_completed && isNearbySuccess && countNearby > 0 && !showNearbyMembers && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                onClick={() => setShowNearbyMembers(true)}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              >
                <PeopleAltIcon sx={{ color: '#656EFF', fontSize: 18, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#656EFF' }}>
                  {countNearby} Member{countNearby > 1 ? 's' : ''} near your pin
                </Typography>
              </Stack>
            )}

            {/* Nearby members list */}
            {showNearbyMembers && nearbyData && (
              <Stack direction="column" gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <PeopleAltIcon sx={{ color: '#656EFF', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
                    {countNearby} Member{countNearby > 1 ? 's' : ''} near your pin
                  </Typography>
                </Stack>
                <Patients
                  patientsData={nearbyData}
                  pageCap={10}
                  align="left"
                  renderPatientCard={(row: any) => (
                    <PatientCard patientInfo={row} align="left" compact key={row.id.toString() + (row?.data_variable ?? '')} />
                  )}
                />
              </Stack>
            )}

            {/* My Pin section */}
            {isProfileSuccess && authStatus === 'authenticated' && loggedInProfile?.is_completed && !showNearbyMembers && (
              <Stack direction="column" gap={1}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <PlaceIcon sx={{ color: '#656EFF', fontSize: 15, flexShrink: 0 }} />
                  <Typography variant="body2" noWrap sx={{ color: '#777', fontSize: 12, flex: 1, minWidth: 0 }}>
                    {loggedInProfile.formattedsearchterm}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Chip
                    icon={<PlaceIcon sx={{ fontSize: 15 }} />}
                    label="My Pin"
                    size="small"
                    variant="outlined"
                    onClick={handleLocatePinClick}
                    sx={{
                      color: '#656EFF',
                      borderColor: 'rgba(101, 110, 255, 0.3)',
                      fontSize: 12,
                      fontWeight: 600,
                      pl: 0.5,
                      '& .MuiChip-icon': { color: '#656EFF' },
                      '& .MuiChip-label': { px: 1 },
                      '&:hover': { backgroundColor: 'rgba(101, 110, 255, 0.08)' },
                    }}
                  />
                  <Chip
                    icon={<EditIcon sx={{ fontSize: 15 }} />}
                    label="Edit"
                    size="small"
                    variant="outlined"
                    onClick={handleEditPinClick}
                    sx={{
                      color: '#656EFF',
                      borderColor: 'rgba(101, 110, 255, 0.3)',
                      fontSize: 12,
                      fontWeight: 600,
                      pl: 0.5,
                      '& .MuiChip-icon': { color: '#656EFF' },
                      '& .MuiChip-label': { px: 1 },
                      '&:hover': { backgroundColor: 'rgba(101, 110, 255, 0.08)' },
                    }}
                  />
                  <Box sx={{ flex: 1 }} />
                  <Chip
                    icon={<NewspaperIcon sx={{ fontSize: 15 }} />}
                    label="News"
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const d = new Date();
                      const query = `Latest treatment developments, science and research on ${condition} as of ${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()}`;
                      navigate('/resources?search=' + encodeURIComponent(query));
                    }}
                    sx={{
                      color: '#D94073',
                      borderColor: 'rgba(217, 64, 115, 0.3)',
                      fontSize: 12,
                      fontWeight: 600,
                      pl: 0.5,
                      '& .MuiChip-icon': { color: '#D94073' },
                      '& .MuiChip-label': { px: 1 },
                      animation: 'newsRing 2.5s ease-in-out infinite',
                      '@keyframes newsRing': {
                        '0%, 100%': { boxShadow: '0 0 0 0 rgba(217, 64, 115, 0)' },
                        '50%': { boxShadow: '0 0 6px 2px rgba(217, 64, 115, 0.35)' },
                      },
                      '&:hover': { animation: 'none', backgroundColor: 'rgba(217, 64, 115, 0.08)' },
                    }}
                  />
                </Stack>
              </Stack>
            )}

            {/* Incomplete profile prompt */}
            {authStatus === 'authenticated' && isProfileSuccess && !loggedInProfile?.is_completed && (
              <Stack direction="column" gap={2} sx={{ p: 2.5, backgroundColor: '#F5F3FF', borderRadius: 3, border: '1px solid #E8E4FF', boxShadow: '0 2px 8px rgba(101, 110, 255, 0.08)' }}>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                  Looking for members nearby? Add your pin to get started!
                </Typography>
                <Button
                  variant="contained"
                  sx={{ borderRadius: 2, backgroundColor: '#656EFF', textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 2.5, boxShadow: '0 2px 8px rgba(101,110,255,0.25)' }}
                  onClick={handleEditPinClick}
                  disableElevation
                  startIcon={<AddLocationAltIcon />}
                >
                  Add My Pin
                </Button>
              </Stack>
            )}

            {/* Unauthenticated: Add My Pin */}
            {authStatus === 'unauthenticated' && isProfileSuccess && (
              <Button
                variant="outlined"
                sx={{ borderRadius: 2, color: '#656EFF', borderColor: '#E0DEFF', textTransform: 'none', fontSize: 13, fontWeight: 500, px: 1.5, alignSelf: 'flex-start' }}
                onClick={() => navigate('/signin/?redirect=/Profile')}
                disableElevation
                startIcon={<AddLocationAltIcon sx={{ fontSize: 16 }} />}
              >
                Add My Pin
              </Button>
            )}
          </Stack>
        </Box>
      </Fade>
    </FloatingContainerMorph>

    </>
  );
};
